import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import PencomTextField from "@/components/input/PencomTextField";
import PencomFileUpload from "@/components/input/PencomFileUpload";
import { PencomRoutes } from "@/router";
import { toast } from "@/utils/toast";
import { useFileUpload } from "@/hooks/file-upload/file-upload.hook";
import { EStorage } from "@/constants/enums/storage.enum";
import {
  useEmployerUpdateRequestPreCheck,
  useSubmitEmployerUpdateRequest,
} from "@/hooks/employer-update-request/employer-update-request.hook";
import { EmployerUpdateRequestSchema } from "@/forms/employer-update-request/employer-update-request.schema";
import { type EmployerUpdateRequestFormValues } from "@/forms/employer-update-request/employer-update-request.form";
import type { EmployerUpdateRequestSubmissionDto } from "@/models/dto/employer-update-request.dto";
import { maskEmail, maskPhone } from "@/utils/maskUtils";
import PendingRequestCard from "./PendingRequestCard";
import RateLimitedCard from "./RateLimitedCard";

export type EmployerUpdateRequestContext = "onboarding" | "authenticated";

interface EmployerUpdateRequestFormProps {
  employerCode: string;
  currentRcNumber?: string;
  currentName?: string;
  currentAddress?: string;
  currentEmail?: string;
  currentPhone?: string;
  currentContactPersonEmail?: string;
  currentContactPersonPhone?: string;
  currentContactPersonRole?: string;
  contactEmailDefault?: string;
  context: EmployerUpdateRequestContext;
  correctionToken?: string;
}

function omitEmpty(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function buildSubmissionPayload(
  values: EmployerUpdateRequestFormValues,
  employerCode: string,
  documentUrl: string,
  originalFileName: string,
  correctionLinkToken?: string
): EmployerUpdateRequestSubmissionDto {
  const newRcNumber = omitEmpty(values.newRcNumber);
  const newName = omitEmpty(values.newName);
  const newAddress = omitEmpty(values.newAddress);
  const newEmail = omitEmpty(values.newEmail);
  const newContactPersonEmail = omitEmpty(values.newContactPersonEmail);
  const newContactPersonPhone = omitEmpty(values.newContactPersonPhone);
  const newContactPersonRole = omitEmpty(values.newContactPersonRole);

  return {
    employerCode,
    currentRcNumber: newRcNumber ? omitEmpty(values.currentRcNumber) : undefined,
    newRcNumber,
    currentName: newName ? omitEmpty(values.currentName) : undefined,
    newName,
    currentAddress: newAddress ? omitEmpty(values.currentAddress) : undefined,
    newAddress,
    currentEmail: newEmail ? omitEmpty(values.currentEmail) : undefined,
    newEmail,
    currentContactPersonEmail: newContactPersonEmail
      ? omitEmpty(values.currentContactPersonEmail)
      : undefined,
    newContactPersonEmail,
    currentContactPersonPhone: newContactPersonPhone
      ? omitEmpty(values.currentContactPersonPhone)
      : undefined,
    newContactPersonPhone,
    currentContactPersonRole: newContactPersonRole
      ? omitEmpty(values.currentContactPersonRole)
      : undefined,
    newContactPersonRole,
    comments: omitEmpty(values.comments),
    documentUrl,
    originalFileName,
    contactEmail: values.contactEmail.trim(),
    correctionLinkToken,
  };
}

/** Extracts a fieldName -> message map (plus a "form" root message) from a Zod SafeParse failure. */
function toFieldErrorMap(
  result: ReturnType<typeof EmployerUpdateRequestSchema.safeParse>
): Record<string, string> | undefined {
  if (result.success) return undefined;
  const errors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key = issue.path.length ? String(issue.path[0]) : "form";
    if (!errors[key]) errors[key] = issue.message;
  }
  return errors;
}

const EmployerUpdateRequestForm = ({
  employerCode,
  currentRcNumber,
  currentName,
  currentAddress,
  currentEmail,
  currentPhone,
  currentContactPersonEmail,
  currentContactPersonPhone,
  currentContactPersonRole,
  contactEmailDefault,
  context,
  correctionToken,
}: EmployerUpdateRequestFormProps) => {
  const navigate = useNavigate();
  const isAuthenticated = context === "authenticated";
  // No authorized contact on file yet means the employer hasn't finished
  // onboarding (EmployerRepPage/createAuthorizedContact hasn't run) — there's
  // nothing to correct, so the contact-person section doesn't apply.
  const isNewEmployer = !currentContactPersonEmail?.trim();

  const preCheck = useEmployerUpdateRequestPreCheck(employerCode);
  const { mutate: submitRequest, isPending: isSubmitting } =
    useSubmitEmployerUpdateRequest(isAuthenticated);
  const { mutate: uploadFile, isPending: isUploadingDocument } =
    useFileUpload();

  const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
  const [documentUrl, setDocumentUrl] = useState("");
  const [originalFileName, setOriginalFileName] = useState("");

  const form = useForm({
    defaultValues: {
      currentRcNumber: currentRcNumber ?? "",
      newRcNumber: "",
      currentName: currentName ?? "",
      newName: "",
      currentAddress: currentAddress ?? "",
      newAddress: "",
      currentEmail: currentEmail ?? "",
      newEmail: "",
      currentContactPersonEmail: currentContactPersonEmail ?? "",
      newContactPersonEmail: "",
      currentContactPersonPhone: currentContactPersonPhone ?? "",
      newContactPersonPhone: "",
      currentContactPersonRole: currentContactPersonRole ?? "",
      newContactPersonRole: "",
      comments: "",
      contactEmail: contactEmailDefault ?? "",
      documentUrl: "",
    } as EmployerUpdateRequestFormValues,
    validators: {
      onSubmit: ({ value }) => {
        const result = EmployerUpdateRequestSchema.safeParse({
          ...value,
          documentUrl,
        });
        return toFieldErrorMap(result);
      },
    },
    onSubmit: async ({ value }) => {
      const payload = buildSubmissionPayload(
        value,
        employerCode,
        documentUrl,
        originalFileName,
        correctionToken
      );

      submitRequest(payload, {
        onSuccess: () => {
          navigate(PencomRoutes.REPORT_SUBMISSION_SUCCESS, {
            state: { context },
          });
        },
        onError: (error) => {
          toast.error(error.message);
          // The most likely cause of a submit-time failure here is a race
          // with the guard (another tab/request just created an open
          // request, or the 72h window just started) — re-run the
          // pre-check so the blocked-state card takes over.
          void preCheck.refetch();
        },
      });
    },
  });

  const handleDocumentSelect = (file: File | null) => {
    setSelectedDocument(file);
    if (!file) {
      setDocumentUrl("");
      setOriginalFileName("");
      return;
    }

    uploadFile(
      { file, path: EStorage.DISCREPANCY, document_type: EStorage.DISCREPANCY },
      {
        onSuccess: (response: unknown) => {
          const payload = response as
            | { data?: { url?: string }; url?: string }
            | string[];
          const url = Array.isArray(payload)
            ? payload[0]
            : payload?.url || payload?.data?.url || "";
          if (!url) {
            toast.error("Failed to upload document. Please try again.");
            setDocumentUrl("");
            setSelectedDocument(null);
            return;
          }
          setDocumentUrl(url);
          setOriginalFileName(file.name);
          toast.success("Document uploaded successfully.");
        },
        onError: (error: Error) => {
          toast.error(error.message || "Failed to upload document.");
          setDocumentUrl("");
          setSelectedDocument(null);
        },
      }
    );
  };

  const handleDocumentRemove = () => {
    setSelectedDocument(null);
    setDocumentUrl("");
    setOriginalFileName("");
  };

  if (preCheck.isLoading) {
    return (
      <div className="w-full flex justify-center py-16">
        <p className="font-clash-display text-sm text-medium-dark-grey">
          Checking your update request status...
        </p>
      </div>
    );
  }

  if (preCheck.data?.blocked && preCheck.data.reason === "OPEN_REQUEST" && preCheck.data.openRequest) {
    return (
      <div className="w-full max-w-[620px]">
        <PendingRequestCard request={preCheck.data.openRequest} />
      </div>
    );
  }

  if (preCheck.data?.blocked && preCheck.data.reason === "RATE_LIMITED") {
    return (
      <div className="w-full max-w-[620px]">
        <RateLimitedCard retryAt={preCheck.data.retryAt} />
      </div>
    );
  }

  const headerTitle =
    context === "onboarding"
      ? "Report Incorrect Company Details"
      : "Request a Company Details Update";
  const headerCaption =
    context === "onboarding"
      ? "Use this form if the company details shown do not match your official records. PenCom will review and contact you if additional documentation is required."
      : "Tell us what needs correcting. PenCom will review your request and email you once it's resolved.";

  return (
    <div className="w-full flex flex-col max-w-[620px]">
      <div className="flex flex-col gap-y-4 mb-6">
        <h1 className="text-[40px] font-semibold text-center font-clash-display text-medium-dark-grey tracking-[-0.6px] leading-tight">
          {headerTitle}
        </h1>
      </div>

      <div className="font-clash-display font-normal text-md text-medium-dark-grey tracking-[-0.08px] mb-6">
        {headerCaption}
      </div>

      <div className="flex flex-col gap-y-6 mb-6">
        <p className="font-clash-display font-semibold text-center text-medium-dark-grey tracking-[-0.08px]">
          Company Details
        </p>
        <div className="bg-white border border-stroke rounded-lg p-4 w-full">
          <div className="flex gap-4 items-center pb-3 border-b border-stroke">
            <div className="bg-pale-mint-green rounded-full size-8 flex items-center justify-center shrink-0">
              <img
                src="/assets/icons/building.svg"
                alt=""
                className="w-6 h-6"
              />
            </div>
            <p className="font-clash-display font-semibold text-xl text-medium-dark-grey">
              {currentName || "—"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 pt-4">
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                RC Number
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {currentRcNumber || "—"}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                Employer Code
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {employerCode || "—"}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                Company Name
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {currentName || "—"}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                Address
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {currentAddress || "—"}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                Contact Email
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {maskEmail(currentEmail ?? "") || "—"}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                Contact Phone Number
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {maskPhone(currentPhone ?? "") || "—"}
              </p>
            </div>
            {!isNewEmployer && (
              <>
                <div className="flex flex-col gap-1">
                  <p className="font-clash-display text-sm text-medium-dark-grey">
                    Contact Person Email
                  </p>
                  <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                    {maskEmail(currentContactPersonEmail ?? "") || "—"}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="font-clash-display text-sm text-medium-dark-grey">
                    Contact Person Phone Number
                  </p>
                  <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                    {maskPhone(currentContactPersonPhone ?? "") || "—"}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="font-clash-display text-sm text-medium-dark-grey">
                    Contact Person Role
                  </p>
                  <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                    {currentContactPersonRole || "—"}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-y-6">
        <form.Subscribe
          selector={(state) => state.errorMap.onSubmit as Record<string, string> | undefined}
          children={(onSubmitErrors) => (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <form.Field
                  name="newRcNumber"
                  children={(field) => (
                    <PencomTextField
                      id="newRcNumber"
                      label="New RC number"
                      type="text"
                      placeholder="Enter the correct RC number"
                      value={field.state.value ?? ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      error={!!onSubmitErrors?.newRcNumber}
                      helperText={onSubmitErrors?.newRcNumber}
                    />
                  )}
                />

                <form.Field
                  name="newName"
                  children={(field) => (
                    <PencomTextField
                      id="newName"
                      label="New company name"
                      type="text"
                      placeholder="Enter the correct company name"
                      value={field.state.value ?? ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      error={!!onSubmitErrors?.newName}
                      helperText={onSubmitErrors?.newName}
                    />
                  )}
                />

                <form.Field
                  name="newAddress"
                  children={(field) => (
                    <PencomTextField
                      id="newAddress"
                      label="New address"
                      type="text"
                      placeholder="Include the house number, street, and city"
                      value={field.state.value ?? ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      error={!!onSubmitErrors?.newAddress}
                      helperText={onSubmitErrors?.newAddress}
                    />
                  )}
                />

                <form.Field
                  name="newEmail"
                  children={(field) => (
                    <PencomTextField
                      id="newEmail"
                      label="New email address"
                      type="email"
                      placeholder="Enter the correct email address"
                      value={field.state.value ?? ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      error={!!onSubmitErrors?.newEmail}
                      helperText={onSubmitErrors?.newEmail}
                    />
                  )}
                />

                {!isNewEmployer && (
                  <>
                    <form.Field
                      name="newContactPersonEmail"
                      children={(field) => (
                        <PencomTextField
                          id="newContactPersonEmail"
                          label="New contact person email"
                          type="email"
                          placeholder="Enter the correct contact person email"
                          value={field.state.value ?? ""}
                          onChange={(e) => field.handleChange(e.target.value)}
                          descriptionText="This updates the email that receives login OTPs for this account."
                          error={!!onSubmitErrors?.newContactPersonEmail}
                          helperText={onSubmitErrors?.newContactPersonEmail}
                        />
                      )}
                    />

                    <form.Field
                      name="newContactPersonPhone"
                      children={(field) => (
                        <PencomTextField
                          id="newContactPersonPhone"
                          label="New contact person phone number"
                          type="text"
                          placeholder="Enter the correct contact person phone number"
                          value={field.state.value ?? ""}
                          onChange={(e) => field.handleChange(e.target.value)}
                          error={!!onSubmitErrors?.newContactPersonPhone}
                          helperText={onSubmitErrors?.newContactPersonPhone}
                        />
                      )}
                    />

                    <form.Field
                      name="newContactPersonRole"
                      children={(field) => (
                        <PencomTextField
                          id="newContactPersonRole"
                          label="New contact person role"
                          type="text"
                          placeholder="Enter the correct contact person role"
                          value={field.state.value ?? ""}
                          onChange={(e) => field.handleChange(e.target.value)}
                          error={!!onSubmitErrors?.newContactPersonRole}
                          helperText={onSubmitErrors?.newContactPersonRole}
                        />
                      )}
                    />
                  </>
                )}
              </div>

              {onSubmitErrors?.form && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600 font-clash-display">
                    {onSubmitErrors.form}
                  </p>
                </div>
              )}

              <form.Field
                name="comments"
                children={(field) => (
                  <PencomTextField
                    id="comments"
                    label="Comments"
                    type="text"
                    multiline
                    rows={4}
                    placeholder="Add any extra context PenCom should know."
                    value={field.state.value ?? ""}
                    onTextareaChange={(e) => field.handleChange(e.target.value)}
                  />
                )}
              />

              <form.Field
                name="contactEmail"
                children={(field) => (
                  <PencomTextField
                    id="contactEmail"
                    label="Contact email for this request"
                    type="email"
                    placeholder="Enter email address"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    descriptionText="PenCom will use this email to respond to your request."
                    error={!!onSubmitErrors?.contactEmail}
                    helperText={onSubmitErrors?.contactEmail}
                  />
                )}
              />
            </>
          )}
        />

        <PencomFileUpload
          id="update-request-document"
          label="Application letter (required)"
          placeholder="Click here or drag and drop to upload"
          accept=".pdf,.jpg,.jpeg,.png"
          maxSize={10}
          onFileSelect={handleDocumentSelect}
          selectedFile={selectedDocument}
          disabled={isUploadingDocument}
          isUploading={isUploadingDocument}
          onFileRemove={handleDocumentRemove}
        />
        <div className="font-clash-display font-medium text-sm text-medium-dark-grey tracking-[-0.03px]">
          <p>
            Please attach an application letter addressing the correction
            being requested. The letter must be written on the
            company&apos;s official letterhead and duly signed by an
            authorized representative.
          </p>
        </div>

        <form.Subscribe
          selector={(state) => state.values}
          children={() => (
            <PrimaryButton
              isLoading={isSubmitting || isUploadingDocument}
              disabled={isSubmitting || isUploadingDocument || !documentUrl}
              className="mt-4"
              title={
                isUploadingDocument
                  ? "Uploading Document..."
                  : "Submit Update Request"
              }
              onClick={() => form.handleSubmit()}
            />
          )}
        />
      </div>
    </div>
  );
};

export default EmployerUpdateRequestForm;
